import React, { useEffect, useRef, useState } from 'react';
import { useGetList } from 'react-admin';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';

import type { FacetGroup } from '@src/facets';

import { hasAnyConditionSet } from './types';
import type { ScopeMode, TemplatePurpose, WizardState } from './types';
import ConditionBuilder from './ConditionBuilder';
import { fetchCatalogItemMatchCount } from './fetchCatalogItemsForTemplate';

interface ScopeStepProps {
  state: WizardState;
  updateState: (patch: Partial<WizardState>) => void;
}

const DEBOUNCE_MS = 400;

const ScopeStep = ({ state, updateState }: ScopeStepProps) => {
  const { data: facetGroups = [] } = useGetList<FacetGroup>('facets');
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isCounting, setIsCounting] = useState(false);
  const generation = useRef(0);

  useEffect(() => {
    // "All catalog records" has nothing to preview — it's trivially the whole catalog, so
    // there's no reason to round-trip to the backend for it. A "create new records" template
    // has nothing to match against either. And a "subset" with no condition defined yet (still
    // just the default empty row) would fail the backend's validation if sent as-is, so skip
    // the fetch until there's a real condition too.
    if (state.purpose !== 'update' || state.scopeMode !== 'subset' || !hasAnyConditionSet(state)) {
      generation.current += 1; // invalidate any in-flight fetch
      setMatchCount(null);
      setTotalCount(null);
      setIsCounting(false);
      return undefined;
    }

    const thisGeneration = (generation.current += 1);
    setIsCounting(true);
    const timer = setTimeout(() => {
      fetchCatalogItemMatchCount(state)
        .then((result) => {
          if (thisGeneration !== generation.current) return;
          setMatchCount(result.count);
          setTotalCount(result.totalCount);
        })
        .catch(() => {
          // Best-effort live preview only — fail silently (e.g. backend query not available yet).
          if (thisGeneration !== generation.current) return;
          setMatchCount(null);
          setTotalCount(null);
        })
        .finally(() => {
          if (thisGeneration === generation.current) setIsCounting(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.purpose, state.scopeMode, state.conditions]);

  const scopeChipLabel = state.scopeMode === 'all' ?
    'All catalog records' :
    (hasAnyConditionSet(state) ? 'Subset of records' : 'Subset — no conditions yet');

  const matchChipLabel = isCounting ?
    'Checking matches…' :
    (matchCount != null && totalCount != null ? `${matchCount} of ${totalCount} records match` : '—');

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 0.5 }}>
        What will you use this template file for?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tell us what the file is for so we only ask the questions that apply.
      </Typography>

      <RadioGroup
        value={state.purpose}
        onChange={(_e, value) => updateState({ purpose: value as TemplatePurpose })}
      >
        <FormControlLabel value="create" control={<Radio />} label="Create new catalog records" />
        <FormControlLabel value="update" control={<Radio />} label="Update existing catalog records" />
      </RadioGroup>

      {state.purpose === 'create' && (
        <Alert severity="info" sx={{ mt: 3 }}>
          The template will contain the header row only. Add one row for each new catalog record you want to create.
        </Alert>
      )}

      {state.purpose === 'update' && (
        <>
          <Divider sx={{ my: 3 }} />

          <Typography variant="body1" sx={{ mb: 0.5 }}>
            Which catalog records would you like to update?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose whether the generated template will include all catalog records for this partner,
            or define conditions for specific records that you&apos;d like to modify.
          </Typography>

          <RadioGroup
            value={state.scopeMode}
            onChange={(_e, value) => updateState({ scopeMode: value as ScopeMode })}
          >
            <FormControlLabel value="all" control={<Radio />} label="All catalog records" />
            <FormControlLabel value="subset" control={<Radio />} label="A subset of records (define conditions)" />
          </RadioGroup>

          {state.scopeMode === 'subset' && (
            <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Build a &quot;when&quot; ruleset just like the Catalog Rules tool. Only records matching
                these conditions appear in the template.
              </Typography>
              <ConditionBuilder
                conditions={state.conditions}
                facetGroups={facetGroups}
                onChange={conditions => updateState({ conditions })}
              />
            </Paper>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Chip label={scopeChipLabel} size="small" />
            {state.scopeMode === 'subset' && <Chip label={matchChipLabel} size="small" />}
          </Box>

          {state.scopeMode === 'subset' && !hasAnyConditionSet(state) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Define at least one condition above to continue — otherwise there&apos;s nothing to scope the
              template to.
            </Alert>
          )}

          {state.scopeMode === 'subset' && hasAnyConditionSet(state) && !isCounting && matchCount === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No catalog records match these conditions. The template would contain the header row only.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default ScopeStep;
